import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Pencil, Plus, Trash2 } from 'lucide-react';
import type { AlertRule } from '@itr/shared';
import { ALERT_SEVERITIES, COMPARATORS, NOTIFY_CHANNELS } from '@itr/shared';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { Badge, Drawer, EmptyState, ErrorState, Skeleton, useFetch, useToast } from '../components/ui';

/* ================================================================== */
/* Alert rules — the thresholds that turn metrics and health into alerts */
/* ================================================================== */

const COMPARATOR_LABEL: Record<(typeof COMPARATORS)[number], string> = {
  gt: '>',
  gte: '≥',
  lt: '<',
  lte: '≤',
  eq: '=',
  neq: '≠',
};

/** Metrics worth suggesting; any metric name the collectors record works. */
const COMMON_METRICS = ['cpu.usage', 'memory.usage', 'disk.usage'];

const SEVERITY_INTENT = { critical: 'bad', warning: 'warn', info: 'info' } as const;

function formatDuration(sec: number): string {
  if (sec === 0) return 'immediately';
  if (sec % 3600 === 0) return `${sec / 3600} h`;
  if (sec % 60 === 0) return `${sec / 60} min`;
  return `${sec} s`;
}

/** "cpu.usage avg > 85 for 10 min" / "health is down for 2 min". */
function describeCondition(r: AlertRule): string {
  const window = r.forSec > 0 ? ` for ${formatDuration(r.forSec)}` : '';
  if (r.type === 'health') return `health is ${r.healthState ?? '?'}${window}`;
  const unit = /usage|pct$/.test(r.metric ?? '') ? '%' : '';
  return `${r.metric ?? '?'} ${r.agg} ${COMPARATOR_LABEL[r.comparator]} ${r.threshold ?? '?'}${unit}${window}`;
}

function describeScope(r: AlertRule): string {
  const s = r.selector;
  const parts: string[] = [];
  if (s.resourceIds.length) parts.push(`${s.resourceIds.length} resource${s.resourceIds.length === 1 ? '' : 's'}`);
  if (s.kinds.length) parts.push(s.kinds.join(', '));
  if (s.tags.length) parts.push(`tags: ${s.tags.join(', ')}`);
  if (s.customerIds.length) parts.push(`${s.customerIds.length} client${s.customerIds.length === 1 ? '' : 's'}`);
  return parts.length ? parts.join(' · ') : 'All resources';
}

export function AlertRulesPage() {
  const { can } = useAuth();
  const toast = useToast();
  const canWrite = can('alert:write');
  const { data, loading, error, reload } = useFetch(() => api.get<AlertRule[]>('/api/alert-rules'), []);
  const [editing, setEditing] = useState<AlertRule | 'new' | null>(null);

  const toggle = async (rule: AlertRule) => {
    try {
      await api.patch(`/api/alert-rules/${rule.id}`, { enabled: !rule.enabled });
      toast(`${rule.name} ${rule.enabled ? 'disabled' : 'enabled'}`, 'good');
      reload();
    } catch (err) {
      toast('Could not change the rule', 'bad', err instanceof Error ? err.message : undefined);
    }
  };

  const remove = async (rule: AlertRule) => {
    if (!confirm(`Delete the rule "${rule.name}"?\n\nAlerts it already raised stay; it just stops raising new ones.`)) return;
    try {
      await api.delete(`/api/alert-rules/${rule.id}`);
      toast(`Deleted ${rule.name}`, 'good');
      reload();
    } catch (err) {
      toast('Could not delete the rule', 'bad', err instanceof Error ? err.message : undefined);
    }
  };

  const rules = [...(data ?? [])].sort((a, b) => Number(b.enabled) - Number(a.enabled) || a.name.localeCompare(b.name));
  const enabledCount = rules.filter((r) => r.enabled).length;

  return (
    <>
      <div className="page-header">
        <div className="grow">
          <div className="page-subtitle" style={{ marginBottom: 2 }}>
            <Link to="/alerts">
              <ArrowLeft size={13} /> Alerts
            </Link>
          </div>
          <h1 className="page-title">Alert rules</h1>
          <div className="page-subtitle">
            {rules.length} rules · {enabledCount} enabled — a rule raises an alert when its condition holds for the whole window
          </div>
        </div>
        {canWrite && (
          <button className="btn btn-primary" onClick={() => setEditing('new')}>
            <Plus size={15} /> New rule
          </button>
        )}
      </div>

      {loading && !data ? (
        <Skeleton height={320} />
      ) : error && !data ? (
        <ErrorState error={error} onRetry={reload} />
      ) : rules.length === 0 ? (
        <EmptyState title="No alert rules" text="Nothing turns metrics into alerts yet. Add a rule to start." />
      ) : (
        <div className="card">
          <div style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead>
                <tr>
                  <th style={{ width: 80 }}>On</th>
                  <th>Rule</th>
                  <th>Condition</th>
                  <th style={{ width: 100 }}>Severity</th>
                  <th>Applies to</th>
                  <th>Notifies</th>
                  {canWrite && <th style={{ width: 96 }} />}
                </tr>
              </thead>
              <tbody>
                {rules.map((r) => (
                  <tr key={r.id} style={{ opacity: r.enabled ? 1 : 0.55 }}>
                    <td>
                      <label className="row" style={{ gap: 6, cursor: canWrite ? 'pointer' : 'default' }}>
                        <input type="checkbox" checked={r.enabled} disabled={!canWrite} onChange={() => void toggle(r)} aria-label={`Enable ${r.name}`} />
                      </label>
                    </td>
                    <td>{r.name}</td>
                    <td className="mono" style={{ fontSize: 12.5 }}>
                      {describeCondition(r)}
                    </td>
                    <td>
                      <Badge intent={SEVERITY_INTENT[r.severity]}>{r.severity}</Badge>
                    </td>
                    <td className="subtle">{describeScope(r)}</td>
                    <td className="subtle">
                      {r.channels.join(', ') || '—'}
                      {r.autoTicket && ' + ticket'}
                    </td>
                    {canWrite && (
                      <td>
                        <div className="row" style={{ gap: 4, flexWrap: 'nowrap' }}>
                          <button className="btn btn-sm" onClick={() => setEditing(r)} title="Edit rule">
                            <Pencil size={13} />
                          </button>
                          <button className="btn btn-sm" onClick={() => void remove(r)} title="Delete rule">
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {editing && (
        <RuleDrawer
          rule={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            reload();
          }}
        />
      )}
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Create / edit                                                       */
/* ------------------------------------------------------------------ */

type Draft = Pick<
  AlertRule,
  'name' | 'enabled' | 'type' | 'severity' | 'metric' | 'agg' | 'comparator' | 'threshold' | 'healthState' | 'forSec' | 'channels' | 'autoTicket'
>;

const NEW_RULE: Draft = {
  name: '',
  enabled: true,
  type: 'metric',
  severity: 'warning',
  metric: 'cpu.usage',
  agg: 'avg',
  comparator: 'gt',
  threshold: 85,
  healthState: 'down',
  forSec: 600,
  channels: ['inapp', 'email', 'push'],
  autoTicket: false,
};

function RuleDrawer({ rule, onClose, onSaved }: { rule: AlertRule | null; onClose: () => void; onSaved: () => void }) {
  const toast = useToast();
  const [draft, setDraft] = useState<Draft>(() => (rule ? { ...rule } : { ...NEW_RULE }));
  const [saving, setSaving] = useState(false);
  const set = <K extends keyof Draft>(key: K, value: Draft[K]) => setDraft((d) => ({ ...d, [key]: value }));

  const save = async () => {
    if (!draft.name.trim()) {
      toast('Give the rule a name', 'warn');
      return;
    }
    if (draft.type === 'metric' && (!draft.metric?.trim() || draft.threshold === null || Number.isNaN(draft.threshold))) {
      toast('A metric rule needs a metric and a threshold', 'warn');
      return;
    }
    // Only the fields this form owns — selector, policy and renotify are kept as they are.
    const body = {
      ...draft,
      name: draft.name.trim(),
      metric: draft.type === 'metric' ? draft.metric?.trim() ?? null : null,
      threshold: draft.type === 'metric' ? draft.threshold : null,
      healthState: draft.type === 'health' ? draft.healthState : null,
    };
    setSaving(true);
    try {
      if (rule) await api.patch(`/api/alert-rules/${rule.id}`, body);
      else await api.post('/api/alert-rules', body);
      toast(rule ? `Saved ${body.name}` : `Created ${body.name}`, 'good');
      onSaved();
    } catch (err) {
      toast('Could not save the rule', 'bad', err instanceof Error ? err.message : undefined);
    } finally {
      setSaving(false);
    }
  };

  const toggleChannel = (ch: (typeof NOTIFY_CHANNELS)[number]) =>
    set('channels', draft.channels.includes(ch) ? draft.channels.filter((c) => c !== ch) : [...draft.channels, ch]);

  return (
    <Drawer
      open
      onClose={onClose}
      title={rule ? `Edit ${rule.name}` : 'New alert rule'}
      footer={
        <div className="row" style={{ gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={() => void save()} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      }
    >
      <div className="field">
        <label className="field-label" htmlFor="rule-name">Name</label>
        <input id="rule-name" className="input" value={draft.name} onChange={(e) => set('name', e.target.value)} placeholder="High CPU" />
      </div>

      <div className="field">
        <label className="field-label" htmlFor="rule-type">Fires on</label>
        <select id="rule-type" className="select" value={draft.type} onChange={(e) => set('type', e.target.value as Draft['type'])}>
          <option value="metric">A metric crossing a threshold</option>
          <option value="health">A resource's health state</option>
        </select>
      </div>

      {draft.type === 'metric' ? (
        <>
          <div className="field">
            <label className="field-label" htmlFor="rule-metric">Metric</label>
            <input
              id="rule-metric"
              className="input mono"
              list="rule-metric-options"
              value={draft.metric ?? ''}
              onChange={(e) => set('metric', e.target.value)}
              placeholder="cpu.usage"
            />
            <datalist id="rule-metric-options">
              {COMMON_METRICS.map((m) => (
                <option key={m} value={m} />
              ))}
            </datalist>
          </div>
          <div className="row" style={{ gap: 8, alignItems: 'flex-end' }}>
            <div className="field" style={{ flex: 1 }}>
              <label className="field-label" htmlFor="rule-agg">Measured as</label>
              <select id="rule-agg" className="select" value={draft.agg} onChange={(e) => set('agg', e.target.value as Draft['agg'])}>
                {(['avg', 'max', 'min', 'last', 'sum'] as const).map((a) => (
                  <option key={a} value={a}>
                    {a === 'avg' ? 'average' : a === 'max' ? 'peak' : a === 'min' ? 'lowest' : a === 'last' ? 'latest' : 'total'}
                  </option>
                ))}
              </select>
            </div>
            <div className="field" style={{ width: 90 }}>
              <label className="field-label" htmlFor="rule-cmp">Is</label>
              <select id="rule-cmp" className="select" value={draft.comparator} onChange={(e) => set('comparator', e.target.value as Draft['comparator'])}>
                {COMPARATORS.map((c) => (
                  <option key={c} value={c}>
                    {COMPARATOR_LABEL[c]}
                  </option>
                ))}
              </select>
            </div>
            <div className="field" style={{ width: 110 }}>
              <label className="field-label" htmlFor="rule-threshold">Threshold</label>
              <input
                id="rule-threshold"
                className="input"
                type="number"
                value={draft.threshold ?? ''}
                onChange={(e) => set('threshold', e.target.value === '' ? null : Number(e.target.value))}
              />
            </div>
          </div>
        </>
      ) : (
        <div className="field">
          <label className="field-label" htmlFor="rule-health">Health becomes</label>
          <select
            id="rule-health"
            className="select"
            value={draft.healthState ?? 'down'}
            onChange={(e) => set('healthState', e.target.value as Draft['healthState'])}
          >
            <option value="down">Down</option>
            <option value="degraded">Degraded</option>
            <option value="unknown">Unknown</option>
          </select>
        </div>
      )}

      <div className="row" style={{ gap: 8 }}>
        <div className="field" style={{ flex: 1 }}>
          <label className="field-label" htmlFor="rule-for">For at least (minutes)</label>
          <input
            id="rule-for"
            className="input"
            type="number"
            min={0}
            value={Math.round(draft.forSec / 60)}
            onChange={(e) => set('forSec', Math.max(0, Math.round(Number(e.target.value) * 60)))}
          />
        </div>
        <div className="field" style={{ flex: 1 }}>
          <label className="field-label" htmlFor="rule-sev">Severity</label>
          <select id="rule-sev" className="select" value={draft.severity} onChange={(e) => set('severity', e.target.value as Draft['severity'])}>
            {ALERT_SEVERITIES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="field">
        <span className="field-label">Notify by</span>
        <div className="row" style={{ gap: 12, flexWrap: 'wrap' }}>
          {NOTIFY_CHANNELS.map((ch) => (
            <label key={ch} className="row" style={{ gap: 5, fontSize: 13 }}>
              <input type="checkbox" checked={draft.channels.includes(ch)} onChange={() => toggleChannel(ch)} />
              {ch === 'inapp' ? 'in-app' : ch}
            </label>
          ))}
        </div>
      </div>

      <div className="field">
        <label className="row" style={{ gap: 6, fontSize: 13 }}>
          <input type="checkbox" checked={draft.autoTicket} onChange={(e) => set('autoTicket', e.target.checked)} />
          Open a ticket when it fires
        </label>
        <label className="row" style={{ gap: 6, fontSize: 13 }}>
          <input type="checkbox" checked={draft.enabled} onChange={(e) => set('enabled', e.target.checked)} />
          Enabled
        </label>
      </div>

      {rule && (
        <p className="subtle" style={{ fontSize: 12 }}>
          Applies to: {describeScope(rule)}.
        </p>
      )}
    </Drawer>
  );
}
